import { Component, OnDestroy, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { Subscription } from 'rxjs';
import { ClaimService } from '../claim.service';
import { ClaimRequest } from '../claim-request';
import { ClaimFormMapper } from '../claim-form-mapper';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { LoadingService } from '../../../shared/loading.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { ClaimCustomerComponent } from '../claim-customer/claim-customer.component';
import { ClaimRepairFacilityComponent } from '../claim-repair-facility/claim-repair-facility.component';
import { ClaimEstimateComponent } from '../claim-estimate/claim-estimate.component';
import { ClaimPicturesComponent } from '../claim-pictures/claim-pictures.component';

@Component({
    selector: 'claim-submission',
    templateUrl: './claim-submission.component.html',
})
export class ClaimSubmissionComponent implements OnDestroy {

    @ViewChild('stepper') stepper: MatStepper;
    @ViewChild('customer') customer: ClaimCustomerComponent;
    @ViewChild('repairFacility') repairFacility: ClaimRepairFacilityComponent;
    @ViewChild('estimate') estimate: ClaimEstimateComponent;
    @ViewChild('pictures') pictures: ClaimPicturesComponent;

    public missingFieldsError: boolean = false;
    public claimServiceSubscription: Subscription;
    public submitClicked: boolean = false;
    public remittanceNumber: number;
    public claimRepairFacilityData: ClaimRequest = null;
    public searchCriteria: string = "";


    public modalMissingFileRef: MatDialogRef<DialogComponent>;

    constructor(private claimService: ClaimService, private router: Router, private loadingService: LoadingService, private dialog: MatDialog) { }

    ngOnDestroy(): void {
        if (this.claimServiceSubscription) {
            this.claimServiceSubscription.unsubscribe();
        }
    }

    public get title() {
        let title: string;

        switch (this.stepper?.selectedIndex) {
            case 0: {
                title = "Customer Information"
                break;
            }
            case 1: {
                title = "Repair Facility Information"
                break;
            }
            default: {
                title = "Customer Information";
            }
        }

        return title
    }

    public backClicked(): void {
        this.stepper.previous();
        this.missingFieldsError = !this.stepper.selected.completed;
    }

    public nextClicked(): void {
        this.missingFieldsError = true;

        if (this.stepper.selectedIndex === 0) {
            this.customer.validateForm();
            this.estimate.validateForm();
            this.stepper.selected.completed = (this.customer.formCompleted && this.estimate.formCompleted);
            this.validateEstimateStep();
        }
        else if (this.stepper.selectedIndex === 1) {
            this.validateRepairFacilityStep();
        }
    }

    private validateEstimateStep() {

        if (this.customer.formCompleted && this.estimate.formCompleted && this.estimate.missingUpload) {
            this.validateEstimateUpload();
        }
        else { this.approveEstimateUpload(); }
    }

    private approveEstimateUpload(): void {
        if (this.stepper.selected.completed) {
            this.stepper.next();
            this.repairFacility.claimRepairFacilityForm.clearValidators();
        }
    }

    private validateRepairFacilityStep(): void {
        this.repairFacility.validateForm();
        this.stepper.selected.completed = this.repairFacility.formCompleted;
    }

    private validateEstimateUpload(): void {
        const dialogResult = this.showDialog('Estimate confirmation',
            'Please note we need cause of failure and a repair estimate to authorize a claim. Are you sure you want to continue without uploading the estimate?',
            'Cancel', 'Yes, Continue');
    }

    public showDialog(title: string, messageText: string, cancelButtonText: string, confirmationButtonText: string, showConfirmation: boolean = true): void {
        this.modalMissingFileRef = this.dialog.open(DialogComponent, {
            data: {
                title: title,
                bodyText: messageText,
                cancelButtonText: cancelButtonText,
                confirmationButtonText: confirmationButtonText,
                showConfirmationButton: showConfirmation
            }
        });
        this.modalMissingFileRef.afterClosed().subscribe(result => {
            if (result) this.approveEstimateUpload();
        });
    }

    public submit(): void {
        if (this.validateAllRequiredData()) {
            this.loadingService.show();
            const estimateFile: File = this.estimate.importFiles[0] ?? null;
            const pictureFiles: File[] = this.pictures?.importFiles ?? [];
            this.consolidateFormData();
            this.saveClaim(estimateFile, pictureFiles);
        }
    }

    private validateAllRequiredData(): boolean {
        if (this.stepper.selectedIndex === 1) {
            this.repairFacility.validateForm();
            this.missingFieldsError = true;
        }
        return (this.customer.formCompleted && this.repairFacility.formCompleted);
    }

    private saveClaim(estimateFile: File, pictureFiles: File[]) {
        if (this.claimRepairFacilityData !== null) {
            this.submitClicked = true;
            this.claimServiceSubscription = this.claimService.saveClaim(estimateFile, pictureFiles, this.claimRepairFacilityData)
                .subscribe({
                    next: response => {
                        this.loadingService.hide();
                        this.router.navigateByUrl("/claims/claim-submitted");
                    },
                    error: error => {
                        this.loadingService.hide();
                        alert('Error submitting');
                        this.submitClicked = false;
                        throw (error);
                    }
                });
        }
        else {
            alert('There was an error submitting the claim. Please try again.');
        }
    }

    public consolidateFormData(): void {
        let newForm = new UntypedFormGroup({});
        this.addControls(newForm, this.customer.claimCustomerForm);
        this.addControls(newForm, this.repairFacility.claimRepairFacilityForm);
        this.addControls(newForm, this.estimate.claimEstimateForm);
        this.claimRepairFacilityData = new ClaimRequest();
        this.claimRepairFacilityData = ClaimFormMapper.mapClaimRequest(newForm);
    }

    public addControls(finalForm: UntypedFormGroup, partialForm: UntypedFormGroup): void {
        Object.keys(partialForm.controls).forEach((key: string) =>
            finalForm.addControl(key, partialForm.controls[key])
        );
    }

    public restart(): void {
        location.reload();
    }
}

